import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { isSuperAdmin } from '../utils/roleManager';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { DEFAULT_AUDIO_PROFILE, VOICE_TYPE_PRESETS, mergeAudioProfile, AudioEnhancementFields, Toggle } from '../components/AudioEnhancementEditor';

function SuperAdminAudioEnhancement() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { userId } = useParams();
  const userIsSuperAdmin = isSuperAdmin(currentUser);

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const [autoEnhance, setAutoEnhance] = useState(false);
  const [profile, setProfile] = useState(DEFAULT_AUDIO_PROFILE);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, 'users', userId));
      if (snap.exists()) {
        const data = snap.data();
        setUser({ id: snap.id, ...data });
        setAutoEnhance(!!data.recordingPreferences?.autoEnhanceRecordings);
        setProfile(mergeAudioProfile(data.recordingPreferences?.audioEnhancementProfile));
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Error loading user:', error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!userIsSuperAdmin) {
      navigate('/');
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIsSuperAdmin, navigate, userId]);

  const updateSection = (section, patch) => {
    setProfile((prev) => ({ ...prev, [section]: { ...prev[section], ...patch } }));
  };

  const updateEqBand = (index, patch) => {
    setProfile((prev) => ({
      ...prev,
      equalizer: {
        ...prev.equalizer,
        bands: prev.equalizer.bands.map((b, i) => (i === index ? { ...b, ...patch } : b)),
      },
    }));
  };

  const applyVoicePreset = (voiceType) => {
    const gains = VOICE_TYPE_PRESETS[voiceType] || VOICE_TYPE_PRESETS.off;
    setProfile((prev) => ({
      ...prev,
      equalizer: {
        enabled: voiceType !== 'off',
        bands: prev.equalizer.bands.map((b, i) => ({ ...b, gain: gains[i] })),
      },
    }));
  };

  const resetToDefaults = () => setProfile(DEFAULT_AUDIO_PROFILE);

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await updateDoc(doc(db, 'users', userId), {
        'recordingPreferences.autoEnhanceRecordings': autoEnhance,
        'recordingPreferences.audioEnhancementProfile': profile,
      });
      setMessage({ type: 'success', text: 'Saved. Takes effect on the next verse this person uploads — already-uploaded recordings are unaffected.' });
    } catch (error) {
      console.error('Error saving audio enhancement profile:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to save.' });
    } finally {
      setSaving(false);
    }
  };

  if (!userIsSuperAdmin) return null;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cerulean" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <p className="text-gray-500">User not found.</p>
        <Link to="/super-admin" className="text-cerulean font-medium mt-4 inline-block">Back to Super Admin</Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      <Link
        to={`/super-admin/users/${userId}`}
        className="text-cerulean hover:text-cerulean/80 text-sm font-medium flex items-center gap-1 w-fit"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        Back to Profile
      </Link>

      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Audio Enhancement — {user.displayName || 'User'}
        </h1>
        <p className="text-sm text-gray-500">{user.email}</p>
      </div>

      {message && (
        <div className={`rounded-xl p-4 text-sm ${
          message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* Master switch */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Auto-Enhance Recordings</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Master switch — off means every stage below is skipped, recordings upload untouched.
            </p>
          </div>
          <Toggle checked={autoEnhance} onChange={setAutoEnhance} />
        </div>
      </div>

      <fieldset disabled={!autoEnhance} className={autoEnhance ? '' : 'opacity-40 pointer-events-none'}>
        <AudioEnhancementFields
          profile={profile}
          updateSection={updateSection}
          updateEqBand={updateEqBand}
          applyVoicePreset={applyVoicePreset}
          currentUser={currentUser}
        />
      </fieldset>

      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          className="px-5 py-2.5 bg-cerulean text-white rounded-lg font-semibold text-sm hover:bg-cerulean/90 transition-colors disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save Profile'}
        </button>
        <button
          onClick={resetToDefaults}
          disabled={saving}
          className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-semibold text-sm transition-colors disabled:opacity-50"
        >
          Reset to Defaults
        </button>
      </div>

      <p className="text-xs text-gray-400">
        This profile only affects new uploads going forward — it doesn't reprocess recordings this person has already uploaded.
      </p>
    </div>
  );
}

export default SuperAdminAudioEnhancement;
